import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import useActivityPageNavigation from '~/bootcamp/hooks/useActivityPageNavigation'
import { ActivityMenu, BootcampPageUrlParamsType, LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import {
  selectBootcampIsCollapsed,
  selectBootcampPage,
  selectBootcampPageActivities,
  selectBootcampPageLevelProgress,
  setBootcampPageData,
} from '~/bootcamp/slices/bootcamp-page.slice'
import { setCurrActivityTypeActionCreator } from '~/bootcamp/slices/bootcamp-activity.slice'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

import { selectHideHorizontalMenuHeight, setHideHorizontalMenuActionCreator } from '~/redux/slices/menu'
import { useGetVideoWatchedPerBootcampQuery } from '~/bootcamp/apis/get-video-watch-list.api'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import { isAuthenticated } from '~/auth/slices/auth.slice'

import CoursePageCollapse from './CoursePage/CoursePageCollapse'
import { checkIfLevelComplete, getActivityTypesCount } from './BootcampView.utils'
import BootcampContent from './BootcampContent/BootcampContent'
import VideoCourseGradeView from './VideoCourseGradeView'
import VideoCourseBootcampView from './VideoCourseBootcampView'

interface Props {
  isEnglishOnly?: boolean
}
const VideoCourse = ({ isEnglishOnly = false }: Props): React.ReactElement => {
  const router = useRouter()
  const { asPath, query } = router
  const authenticated = useAppSelector(isAuthenticated)

  const {
    action,
    key: bootcampKey,
    moduleKey,
    subtopic,
    type: activityType,
  } = query as unknown as BootcampPageUrlParamsType

  const { data: videoWatchedData } = useGetVideoWatchedPerBootcampQuery(
    {
      topic: subtopic,
      subject: bootcampKey,
      bootcampId: moduleKey,
    },
    { skip: !authenticated },
  )

  const asPathWithoutQuery = asPath.split('?')[0]

  const dispatch = useAppDispatch()
  const bootcampContentRef = useRef<HTMLDivElement>(null)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const bottomNavbarHeight = useAppSelector(selectHideHorizontalMenuHeight)

  const courseLevels = useActivityPageNavigation()
  const courseLevelKeys = Object.keys(courseLevels)
  const levelProgress = useAppSelector(selectBootcampPageLevelProgress)
  const isCollapsed = useAppSelector(selectBootcampIsCollapsed)

  const activities = useAppSelector(selectBootcampPageActivities) || []
  const activityTypesCount = getActivityTypesCount(activities)

  const isGradePage = useIsBootcampGradePage()
  const pageData = useAppSelector(selectBootcampPage)
  useEffect(() => {
    if (action === LevelActionType.Activity) {
      const actType = parseInt(activityType as string)
      dispatch(setCurrActivityTypeActionCreator(actType))
    }
    if (isEnglishOnly !== pageData.isEnglishOnly) {
      dispatch(setBootcampPageData({ ...pageData, isEnglishOnly }))
    }
  }, [action, pageData, dispatch, router, activityType, activityTypesCount, isEnglishOnly])

  useEffect(() => {
    function handleScroll(): void {
      const scrollTop = bootcampContentRef.current?.getBoundingClientRect().top || 0
      const nodeHeight = bootcampContentRef.current?.parentElement?.parentElement?.clientHeight || 0
      const screenHeight = window?.innerHeight
      const topPosition = -1 * (scrollTop - 84) // padding 20px and header 70px
      /**
       * Hide nav when:
       * 1. node height is greater than screen height
       * 2. top position is greater than 0
       * 3. bottom scroll threshold is reached
       */
      const shouldHideNav =
        nodeHeight + bottomNavbarHeight > screenHeight - 24 &&
        topPosition > 0 &&
        nodeHeight + bottomNavbarHeight > screenHeight + topPosition
      dispatch(setHideHorizontalMenuActionCreator(shouldHideNav))
    }
    if (isMobile && action === LevelActionType.Activity && bootcampContentRef && bootcampContentRef.current) {
      window?.addEventListener('scroll', handleScroll)

      return () => {
        window?.removeEventListener('scroll', handleScroll)
      }
    }
  }, [dispatch, isMobile, action, bottomNavbarHeight])

  const renderPageCards = ({ title, type, level, index, icon }: ActivityMenu, idx: number): React.ReactElement => {
    const pagePath = `/bootcamp/${subtopic}/${bootcampKey}/${moduleKey}/${level}/${type}${
      type === LevelActionType.Activity ? `/${index}` : ''
    }`

    const isCompleted =
      checkIfLevelComplete(
        levelProgress?.processedLeafs,
        `${level}-${type}${type === LevelActionType.Activity ? `-${index}` : ''}`,
      ) || Boolean(videoWatchedData?.includes(pagePath))

    const pageCard = (
      <CoursePageCollapse
        currentPath={asPathWithoutQuery}
        icon={icon ?? type}
        isCollapsed={isCollapsed}
        isCompleted={isCompleted}
        key={`${type}-${level}-${idx}`}
        level={+level}
        path={pagePath}
        title={title}
        type={type}
      >
        <div ref={bootcampContentRef}>
          <BootcampContent isVideoWatched={isCompleted} />
        </div>
      </CoursePageCollapse>
    )

    if (type === LevelActionType.Quiz) {
      return (
        <div key={`${type}-${level}-${idx}`} style={{ minHeight: 700 }}>
          {pageCard}
        </div>
      )
    }

    return pageCard
  }

  if (isGradePage) {
    return (
      <VideoCourseGradeView
        courseLevelKeys={courseLevelKeys}
        courseLevels={courseLevels}
        renderPageCards={renderPageCards}
      />
    )
  }

  return (
    <VideoCourseBootcampView
      courseLevelKeys={courseLevelKeys}
      courseLevels={courseLevels}
      renderPageCards={renderPageCards}
    />
  )
}

export default VideoCourse
